import type { FormDefinition } from '@/api/types/Forms'
import type { FormStates } from './stepState'
import { isStepVisible } from './visibility'

/** The id of the next step the patient should see, or null when the form is finished. */
export const nextVisibleStepId = (
	definition: FormDefinition,
	currentStepId: number,
	states: FormStates
): number | null => {
	const next = definition.steps.find(
		(step) => step.id > currentStepId && isStepVisible(step, states)
	)
	return next?.id ?? null
}

/** The id of the step before this one that is shown, or null from the first step. */
export const prevVisibleStepId = (
	definition: FormDefinition,
	currentStepId: number,
	states: FormStates
): number | null => {
	const earlier = definition.steps.filter(
		(step) => step.id < currentStepId && isStepVisible(step, states)
	)
	return earlier[earlier.length - 1]?.id ?? null
}

/** Where a step sits in its form, and how far along that is. */
export type FormProgress = { position: number; total: number; fraction: number }

/**
 * A step's place in the form: its position in the list, counting from one, and the share of the
 * form that position represents. Measured by index rather than by id, which is not a count and
 * need not start at one.
 */
export const stepProgress = (
	definition: FormDefinition,
	currentStepId: number
): FormProgress => {
	const total = definition.steps.length
	const index = definition.steps.findIndex((step) => step.id === currentStepId)
	const position = index < 0 ? 0 : index + 1
	return { position, total, fraction: total === 0 ? 0 : position / total }
}
